import { defineStore } from "pinia";
import { computed, ref } from "vue";
import fallbackConfig from "@/assets/template/config.json";
import http from "@/lib/http";
import type { AdvancedApiConfig } from "@/lib/advanced-features";

export interface VersionConfig {
  label: string;
  value: string;
  url: string;
}
interface SystemConfig {
  NEXTBACK_HOME: string;
  SANDBOX_URL: string;
  PRODUCTION_URL: string;
  VERSION_CONFIG_LIST: VersionConfig[];
}

export const useSystemStore = defineStore("system", () => {
  // 状态
  const _systemConfig = ref<SystemConfig | null>(null);
  const _advancedApiConfig = ref<AdvancedApiConfig[]>([]);
  const _loading = ref(false);

  // 计算属性
  const systemInfo = computed<ImportMetaEnv>(() => import.meta.env); // 系统级配置信息
  const apiBaseUrl = computed(() => systemInfo.value?.VITE_API_BASE_URL);
  const appName = computed(() => systemInfo.value?.VITE_APP_NAME);
  const systemConfig = computed(() => _systemConfig.value);
  const advancedApiConfig = computed(() => _advancedApiConfig.value);
  const nextbackHome = computed(
    () =>
      _systemConfig.value?.NEXTBACK_HOME ||
      "https://nexbankapi.com/nexbank/%E9%93%B6%E4%BC%81%E9%80%9A/"
  );
  const sandboxUrl = computed(
    () => _systemConfig.value?.SANDBOX_URL || "https://sandbox.nexbankapi.com"
  );
  const productionUrl = computed(
    () => _systemConfig.value?.PRODUCTION_URL || "https://open.nexbankapi.com"
  );
  const loading = computed(() => _loading.value);
  // 方法
  async function fetchSystemConfig() {
    _loading.value = true;
    try {
      const config = await http.get<SystemConfig>("/nxb/config/document");
      if (config && Object.keys(config).length > 0) {
        _systemConfig.value = config;
        return config;
      }
      const fallback = fallbackConfig;
      _systemConfig.value = fallback;
      return fallback;
    } catch (error) {
      console.error("Failed to fetch system config:", error);
      const fallback = fallbackConfig;
      _systemConfig.value = fallback;
      return fallback;
    } finally {
      _loading.value = false;
    }
  }

  async function fetchAdvancedApiConfig() {
    try {
      const config = await http.get<AdvancedApiConfig[]>(
        "/common/keyValue/get",
        { keyType: "advancedApiConfig" },
        {
          ignoreError: true,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      _advancedApiConfig.value = Array.isArray(config) ? config : [];
      return _advancedApiConfig.value;
    } catch (error) {
      console.error("Failed to fetch advanced API config:", error);
      _advancedApiConfig.value = [];
      return [];
    }
  }

  return {
    apiBaseUrl,
    appName,
    systemConfig,
    advancedApiConfig,
    nextbackHome,
    sandboxUrl,
    productionUrl,
    loading,
    fetchSystemConfig,
    fetchAdvancedApiConfig,
  };
});
